import { Component, Injector } from '@angular/core';
import type { OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { Constants } from '@shared/models';
import type { TMessage } from '@shared/models';
import { ViewBasePage } from '@shared/pages';
import { MessageCenterService } from '@shared/services';

@Component({
    'selector': 'app-message',
    'templateUrl': './message.page.html',
    'styleUrls': ['./message.page.scss'],
    'standalone': true,
    'imports': [CommonModule, FormsModule, IonicModule]
})
export class MessagePage extends ViewBasePage implements OnInit {
    public message: TMessage | null = null;

    constructor(
        private messageCenterService: MessageCenterService,
        private activatedRoute: ActivatedRoute,
        injector: Injector
    ) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(Constants.VIEW_MESSAGE);
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();

        const uid = this.activatedRoute.snapshot.paramMap.get('uid');
        if (uid) {
            this.message = await this.messageCenterService.getByUid(uid) || null;
            if (this.message) {
                await this.messageCenterService.markAsRead(uid);
            }
        }
    }

    /**
     * Returns the Ionic color for a given message type.
     */
    public getTypeColor(type: string): string {
        switch (type) {
            case 'warning': return 'warning';
            case 'action': return 'danger';
            case 'announcement': return 'tertiary';
            case 'info':
            default: return 'primary';
        }
    }

    /**
     * Returns the icon name for a message.
     */
    public getTypeIcon(message: TMessage): string {
        if (message.icon) return message.icon;
        switch (message.type) {
            case 'warning': return 'alert-circle';
            case 'action': return 'flash';
            case 'announcement': return 'megaphone';
            case 'info':
            default: return 'information-circle';
        }
    }

    /**
     * Returns a formatted date string.
     */
    public formatDate(date: any): string {
        if (date instanceof Date) {
            return date.toLocaleDateString();
        }
        return '';
    }

    /**
     * Navigate to the action URL, if defined.
     */
    public onActionClick(): void {
        if (this.message?.actionUrl) {
            this.router.navigateByUrl(this.message.actionUrl);
        }
    }

    /**
     * Dismiss this message and navigate back.
     */
    public async onDismissClick(): Promise<void> {
        if (this.message) {
            await this.messageCenterService.dismiss(this.message.uid);
        }
        this.router.navigateByUrl('/help/messageCenter');
    }
}
